import React, { useCallback } from "react";
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Text,
    Spinner,
    makeStyles,
    tokens,
} from "@fluentui/react-components";
import {
    CheckmarkCircle20Regular,
    Info20Regular,
    Warning20Regular,
    ErrorCircle20Regular,
    Delete20Filled,
} from "@fluentui/react-icons";

// ── Variant config ─────────────────────────────────────────────────────────────

const VARIANT_CONFIG = {
    confirm: {
        Icon: CheckmarkCircle20Regular,
        iconColor: tokens.colorBrandForeground1,
        confirmAppearance: "primary",
        defaultConfirmText: "Confirm",
    },
    delete: {
        Icon: Delete20Filled,
        iconColor: tokens.colorPaletteRedForeground1,
        confirmAppearance: "primary",
        confirmButtonColor: "danger",
        defaultConfirmText: "Delete",
    },
    info: {
        Icon: Info20Regular,
        iconColor: tokens.colorBrandForeground1,
        confirmAppearance: "primary",
        defaultConfirmText: "OK",
    },
    warning: {
        Icon: Warning20Regular,
        iconColor: tokens.colorStatusWarningForeground1,
        confirmAppearance: "primary",
        defaultConfirmText: "Proceed",
    },
    error: {
        Icon: ErrorCircle20Regular,
        iconColor: tokens.colorPaletteRedForeground1,
        confirmAppearance: "primary",
        defaultConfirmText: "OK",
    },
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
    surface: {
        width: "460px",
        maxWidth: "95vw",
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    icon: {
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
    },
    message: {
        color: tokens.colorNeutralForeground2,
        fontSize: "14px",
        lineHeight: "20px",
        marginTop: "4px",
    },
    actions: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "8px",
        marginTop: "8px",
    },
    dangerButton: {
        backgroundColor: tokens.colorPaletteRedBackground3,
        color: tokens.colorNeutralForegroundOnBrand,
        border: "none",
        ":hover": {
            backgroundColor: tokens.colorPaletteRedForeground1,
            color: tokens.colorNeutralForegroundOnBrand,
        },
        ":active": {
            backgroundColor: tokens.colorPaletteRedForeground2,
        },
    },
});

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * ConfirmationModal — reusable enterprise confirmation/alert dialog.
 *
 * @param {object}          props
 * @param {boolean}         props.isOpen           - Whether the dialog is visible
 * @param {string}          props.title            - Dialog heading
 * @param {string|ReactNode} props.message         - Body text or custom ReactNode
 * @param {'confirm'|'delete'|'info'|'warning'|'error'} [props.type='confirm']
 * @param {function}        [props.onConfirm]      - Called when confirm button clicked
 * @param {function}        [props.onCancel]       - Called when cancel / ESC / backdrop clicked
 * @param {string}          [props.confirmText]    - Overrides default confirm button label
 * @param {string}          [props.cancelText='Cancel'] - Cancel button label
 * @param {boolean}         [props.confirmDisabled=false] - Disables the confirm button
 * @param {boolean}         [props.loading=false]  - Shows spinner on confirm button
 * @param {boolean}         [props.closeOnBackdrop=true]  - Whether clicking the backdrop closes
 * @param {ReactNode}       [props.footer]         - Replaces the default action buttons
 */
export default function ConfirmationModal({
    isOpen,
    title,
    message,
    type = "confirm",
    onConfirm,
    onCancel,
    confirmText,
    cancelText = "Cancel",
    confirmDisabled = false,
    loading = false,
    closeOnBackdrop = true,
    footer,
}) {
    const styles = useStyles();
    const config = VARIANT_CONFIG[type] ?? VARIANT_CONFIG.confirm;
    const { Icon, iconColor, confirmAppearance, confirmButtonColor, defaultConfirmText } = config;
    const resolvedConfirmText = confirmText ?? defaultConfirmText;
    const isDanger = type === "delete";

    const handleOpenChange = useCallback(
        (_, data) => {
            if (!data.open) {
                // data.type === 'backdropClick' | 'escapeKeyDown' | undefined
                if (!closeOnBackdrop && data.type === "backdropClick") return;
                onCancel?.();
            }
        },
        [closeOnBackdrop, onCancel],
    );

    return (
        <Dialog
            open={isOpen}
            onOpenChange={handleOpenChange}
            modalType="alert"
        >
            <DialogSurface className={styles.surface}>
                <DialogBody>
                    {/* ── Title row with icon ───────────────────────────────── */}
                    <DialogTitle>
                        <div className={styles.titleRow}>
                            <span className={styles.icon} style={{ color: iconColor }}>
                                <Icon fontSize={22} />
                            </span>
                            <Text weight="semibold" size={400}>
                                {title}
                            </Text>
                        </div>
                    </DialogTitle>

                    {/* ── Body ─────────────────────────────────────────────── */}
                    <DialogContent>
                        {typeof message === "string" ? (
                            <Text className={styles.message}>{message}</Text>
                        ) : (
                            message
                        )}
                    </DialogContent>

                    {/* ── Footer ───────────────────────────────────────────── */}
                    <DialogActions>
                        {footer ?? (
                            <div className={styles.actions}>
                                {/* Show cancel only when there is an onCancel handler or it's
                                    not a pure info/error acknowledgement */}
                                {onCancel && (
                                    <Button
                                        appearance="subtle"
                                        onClick={onCancel}
                                        disabled={loading}
                                    >
                                        {cancelText}
                                    </Button>
                                )}

                                {onConfirm && (
                                    <Button
                                        appearance={confirmAppearance}
                                        className={isDanger ? styles.dangerButton : undefined}
                                        onClick={onConfirm}
                                        disabled={confirmDisabled || loading}
                                        icon={loading ? <Spinner size="tiny" /> : undefined}
                                        autoFocus
                                    >
                                        {resolvedConfirmText}
                                    </Button>
                                )}
                            </div>
                        )}
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}
